// Record a screening check on a person: who looked, on what day, and the proof,
// in one step. The proof can be a file already in their Drive folder (a PDF the
// office saved there by hand) or a new upload. Nothing here decides what a check
// is: that is lib/screeningChecks.js, and the write is recordScreeningCheck.
import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useAuth } from '../../hooks/useAuth';
import { SCREENING_KINDS } from '../../lib/screeningChecks';
import { logOnboardingEvent, recordScreeningCheck } from '../../lib/staffCompliance';
import { filesUnder, listStaffFolder, uploadToStaffFolder } from '../../lib/driveListing';

// New proof goes with the rest of their compliance paperwork.
const PROOF_FOLDER = 'Training & Compliance';

const byNewest = (a, b) => String(b.modifiedTime).localeCompare(String(a.modifiedTime));

export default function RecordCheckModal({ staff, kind, onClose, onRecorded }) {
  const spec = SCREENING_KINDS[kind];
  const { profile } = useAuth();
  const today = format(new Date(), 'yyyy-MM-dd');
  const [checkedOn, setCheckedOn] = useState(today);
  const [checkedBy, setCheckedBy] = useState(profile?.full_name || '');
  const [onFile, setOnFile] = useState(null);       // files already in their folder; null while reading
  const [readErr, setReadErr] = useState('');
  const [picked, setPicked] = useState(() => new Set());
  const [uploads, setUploads] = useState([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let active = true;
    if (!staff.employee_id) { setOnFile([]); return undefined; }
    listStaffFolder(staff.id)
      .then((listing) => {
        if (!active) return;
        const files = [...(listing.files || []), ...(listing.folders || []).flatMap((f) => filesUnder(f).map((x) => ({ ...x, where: f.name })))];
        setOnFile(files.sort(byNewest));
      })
      .catch((e) => { if (active) { setOnFile([]); setReadErr(e.message); } });
    return () => { active = false; };
  }, [staff.id, staff.employee_id]);

  const toggle = (id) => setPicked((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const proofCount = picked.size + uploads.length;
  const later = staff[spec.atField] && checkedOn < String(staff[spec.atField]).slice(0, 10);

  const save = async () => {
    if (!checkedBy.trim()) { setError('Say who ran the check.'); return; }
    setBusy(true); setError('');
    try {
      const proofs = (onFile || []).filter((f) => picked.has(f.id)).map((f) => ({ name: f.name, url: f.webViewLink }));
      for (const file of uploads) {
        const { webViewLink } = await uploadToStaffFolder(staff.id, PROOF_FOLDER, file);
        proofs.push({ name: file.name, url: webViewLink });
      }
      const logEvent = (id, type, detail, prev, next) => logOnboardingEvent(id, type, detail, prev, next, profile?.full_name);
      const patch = await recordScreeningCheck(staff.id, kind, checkedBy.trim(), logEvent, { checkedOn, proofs });
      onRecorded(patch);
    } catch (e) {
      setError(e.message);
      setBusy(false);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="modal" onMouseDown={(e) => e.stopPropagation()} style={{ maxWidth: 520 }}>
        <div className="modal-header">
          <div className="modal-title">{spec.label}: {staff.full_name}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>✕</button>
        </div>
        <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
          {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 12, color: 'var(--danger)' }}>{error}</div>}
          <div style={{ fontSize: 12.5, color: 'var(--text2)', marginBottom: 14 }}>{spec.detail}.</div>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
            <div className="form-group" style={{ flex: '1 1 150px' }}>
              <label className="form-label">Date checked</label>
              <input type="date" className="form-input" value={checkedOn} max={today} onChange={(e) => setCheckedOn(e.target.value)} />
            </div>
            <div className="form-group" style={{ flex: '2 1 200px' }}>
              <label className="form-label">Checked by</label>
              <input className="form-input" value={checkedBy} onChange={(e) => setCheckedBy(e.target.value)} />
            </div>
          </div>
          {later && <div style={{ fontSize: 12, color: 'var(--warn)', marginBottom: 12 }}>A later check is already recorded ({format(new Date(staff[spec.atField]), 'MMM d, yyyy')}). Saving this date replaces it as the latest.</div>}

          <div className="form-label" style={{ marginBottom: 6 }}>Proof</div>
          {staff.employee_id && (
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, marginBottom: 10, maxHeight: 190, overflowY: 'auto' }}>
              {onFile === null && <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text3)' }}>Reading their Drive folder…</div>}
              {readErr && <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--danger)' }}>{readErr}</div>}
              {onFile?.length === 0 && !readErr && <div style={{ padding: '10px 12px', fontSize: 12, color: 'var(--text3)' }}>Nothing in their folder yet.</div>}
              {onFile?.map((f) => (
                <label key={f.id} style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '7px 12px', borderTop: '1px solid var(--border)', fontSize: 12.5, color: 'var(--text)', cursor: 'pointer' }}>
                  <input type="checkbox" checked={picked.has(f.id)} onChange={() => toggle(f.id)} style={{ marginTop: 2 }} />
                  <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                    {f.name}
                    <span style={{ display: 'block', fontSize: 11, color: 'var(--text3)' }}>{f.where ? `${f.where} · ` : ''}{format(new Date(f.modifiedTime), 'MMM d, yyyy')}</span>
                  </span>
                </label>
              ))}
            </div>
          )}
          <label className="form-label" style={{ fontWeight: 400 }}>Or add a new file{staff.employee_id ? ` (filed under ${PROOF_FOLDER})` : ' (filed with their application until they have an E-number)'}</label>
          <input type="file" multiple onChange={(e) => setUploads(Array.from(e.target.files || []))} style={{ fontSize: 12, marginBottom: 8 }} />
          {proofCount === 0 && <div style={{ fontSize: 12, color: 'var(--warn)' }}>With no proof attached, this shows as recorded but not proven.</div>}
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
          <button className="btn btn-primary" onClick={save} disabled={busy || !checkedOn}>{busy ? 'Saving…' : 'Record check'}</button>
        </div>
      </div>
    </div>
  );
}
